import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { apiRequest } from '@/lib/queryClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { MessageSquare, Clock, TrendingUp, CheckCircle2, AlertCircle, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';

const ITEMS_PER_PAGE = 10;

export default function HelpdeskAdmin() {
  const [statusFilter, setStatusFilter] = useState('open');
  const [currentPage, setCurrentPage] = useState(1);
  const [, setLocation] = useLocation();

  const { data: tickets = [] } = useQuery<any>({
    queryKey: ['helpdesk-tickets-admin'],
    queryFn: () => apiRequest('GET', '/api/helpdesk')
  });

  const { data: stats } = useQuery<any>({
    queryKey: ['helpdesk-stats'],
    queryFn: () => apiRequest('GET', '/api/helpdesk/stats/overview')
  });

  const formatWaitTime = (ms: number) => {
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60))}m`;
    return `${Math.floor(ms / (1000 * 60))}m`;
  };

  const getCurrentWaitTime = (ticket: any) => {
    if (!ticket.waitingForResponse || !ticket.responseTimerStart) return ticket.totalWaitTime || 0;
    const current = new Date().getTime() - new Date(ticket.responseTimerStart).getTime();
    return (ticket.totalWaitTime || 0) + current;
  };

  // Open tickets first, then those still waiting on us — and within each group NEWEST
  // first. The previous sort put the longest-waiting ticket at the top, which buried
  // every fresh ticket under months-old open ones: with 18 stale open tickets, a new
  // customer report landed at position 18, on page 2, and was nearly missed (2026-09-14).
  const filteredTickets = tickets
    .filter((t: any) => statusFilter === 'all' || t.status === statusFilter)
    .sort((a: any, b: any) => {
      if (a.status === 'open' && b.status !== 'open') return -1;
      if (a.status !== 'open' && b.status === 'open') return 1;

      const aWaiting = a.waitingForResponse && a.responseTimerStart;
      const bWaiting = b.waitingForResponse && b.responseTimerStart;

      if (aWaiting && !bWaiting) return -1;
      if (!aWaiting && bWaiting) return 1;

      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });

  const totalPages = Math.ceil(filteredTickets.length / ITEMS_PER_PAGE);
  const paginatedTickets = filteredTickets.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const statusColors: any = {
    open: 'bg-blue-500',
    in_progress: 'bg-yellow-500',
    closed: 'bg-green-500'
  };

  const handleFilterChange = (value: string) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  return (
    <div className="container mx-auto py-6">
      <h1 className="text-xl sm:text-3xl font-bold mb-6">Helpdesk Management</h1>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Open Tickets</CardTitle>
            <AlertCircle className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.open || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">In Progress</CardTitle>
            <Loader2 className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.inProgress || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Closed</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.closed || 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Avg Response Time</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatWaitTime(stats?.avgWaitTimeMs || 0)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filter Dropdown */}
      <div className="flex justify-between items-center mb-4">
        <Select value={statusFilter} onValueChange={handleFilterChange}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tickets</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="closed">Closed</SelectItem>
          </SelectContent>
        </Select>
        <div className="text-sm text-muted-foreground">
          Showing {paginatedTickets.length} of {filteredTickets.length} tickets
        </div>
      </div>

      {/* Tickets List */}
      <div className="grid gap-4 mb-6">
        {paginatedTickets.map((ticket: any) => (
          <Card 
            key={ticket._id} 
            className="cursor-pointer hover:shadow-md transition-shadow" 
            onClick={() => setLocation(`/helpdesk-ticket/${ticket._id}`)}
          >
            <CardHeader>
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <CardTitle className="text-lg">{ticket.subject}</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    {ticket.userName} ({ticket.userEmail})
                  </p>
                  <div className="flex gap-2 mt-2">
                    <Badge variant="outline" className="capitalize">{ticket.category}</Badge>
                    <Badge className={statusColors[ticket.status]}>{ticket.status.replace('_', ' ')}</Badge>
                    {ticket.waitingForResponse && (
                      <Badge variant="destructive" className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Waiting {formatWaitTime(getCurrentWaitTime(ticket))}
                      </Badge>
                    )}
                  </div>
                </div>
                <MessageSquare className="h-5 w-5 text-muted-foreground" />
              </div>
            </CardHeader>
          </Card>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
